import axios from "axios";
import { history } from "./routes";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

api.interceptors.request.use(config => {
  if (localStorage.getItem("access_token")) {
    config.headers.Authorization = `Bearer ${localStorage.getItem(
      "access_token"
    )}`;
  }

  config.headers["Accept-Language"] =
    localStorage.getItem("stored_lang") || "pt-BR";

  return config;
});

api.interceptors.response.use(
  response => {
    // TODO: The API should be returning a proper response with the right status
    // code so we don't have to do this cleanup here
    if (response.data.code && ![200, 201].includes(response.data.code)) {
      if (response.data.code === 401) {
        localStorage.removeItem("access_token");
        history.push("/login");
      }

      // Replicate an actual axiosError. isAxiosError and toJSON have been left out
      // axiosError definition: https://github.com/axios/axios/blob/521444513969a08ec5ef943c41ba0812845ed4f9/lib/core/enhanceError.js#L23
      const error = new Error();
      error.config = response.config;
      error.code = response.code;
      error.request = response.request;
      error.response = response;
      throw error;
    }

    return response;
  },
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem("@BusCAR/dashboard-access");
      localStorage.removeItem("access_token");
      history.push("/login");
    }

    return Promise.reject(error);
  }
);

export const resetPasswordRequest = (token, password) =>
  api.post(
    "/users/reset_password",
    {
      password
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    }
  );

export const forgotPasswordRequest = email =>
  api.post("/users/forgot_password", {
    email
  });

export const getEstimatedBudget = () =>
  api.get("/b3_criterios/estimated_budget_fixed_price");

export const checkout = async (amount, config) => {
  if (!amount || isNaN(amount) || amount <= 0) {
    return false;
  }

  const { onSuccess, onClose, onError, customerData } = config;

  try {
    // Step 1: Get order from backend (creates order in Pagar.me V5)
    const orderResponse = await getCheckoutOrder(amount, customerData);
    const orderData = orderResponse.data;

    // Step 2: Check if checkout_url is provided (V5 hosted checkout)
    if (orderData.checkout_url) {
      // V5 Approach: Redirect to hosted checkout page
      window.location.href = orderData.checkout_url;
      return;
    }

    // Fallback: Use JavaScript modal (legacy V1 approach)
    // Note: This may not work with V5 API keys
    if (window.PagarMeCheckout && window.PagarMeCheckout.Checkout) {
      const checkout = new window.PagarMeCheckout.Checkout({
        encryption_key: orderData.encryption_key,
        success: onSuccess,
        error: onError,
        close: onClose
      });

      checkout.open({
        orderId: orderData.order_id
      });
    } else {
      throw new Error('Checkout URL não disponível e PagarMeCheckout não carregado');
    }
  } catch (error) {
    // If order creation fails, call error callback
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

export const getCheckoutConfig = () => {
  return api.get("/b3_transacoes/get_pagarme_config");
};

export const getCheckoutOrder = (amount, customerData) => {
  return api.post("/b3_transacoes/create_pagarme_order", {
    amount,
    customer: customerData
  });
};

export const completeCheckout = (url, transaction_token, amount) => {
  return api.post(url, {
    transaction_token,
    amount
  });
};

export const getBalance = () => api.get("/b3_transacoes/get_balance");

export const getHasBalance = budget =>
  api.get("/b3_transacoes/has_enough_balance", { params: { budget } });

export const getAnalysisHistory = (page, limit, { isEnterpriseEnv = false, conditions }) => {
  const params = {
    page,
    limit
  };

  if (isEnterpriseEnv && conditions) {
    params['options'] = {
      conditions
    }
  }

  const url = isEnterpriseEnv
    ? `/b3_consultas/query_enterprise`
    : `/b3_consultas/query`

  return api.get(url, { params });
}

export const filterHistory = (page, limit, busca) => {
  const params = {
    page,
    limit,
    busca
  };

  return api.get("/b3_consultas/query", { params });
};

export const getPlanEnterprise = () =>
  api.get('b3_consultas/get_plan_enterprise')

export const getResults = id => api.get(`/b3_consultas/get_results/${id}`);

export const getResultsMapLayers = resultId =>
  api.get(`/b3_consultas/get_results_map_layers/${resultId}`);

export const getAreaInfo = area_id =>
  api.get(`/b3_areas/search_aoi_info/${area_id}`);

export const accountConfirmationRequest = token =>
  api.get(`users/validate_email/${token}`);

export const userLogin = (email, password) => {
  return api.post("/users/login", { username: email, password });
};

export const processAnalysis = (consulta_id, { isEnterpriseEnv = false }) => {
  const url = isEnterpriseEnv
    ? `/b3_consultas/process_analysis_enterprise`
    : `/b3_consultas/process_analysis`

  return api.post(url, { consulta_id });
}

export const generateResultPdf = (b3_consulta_id, criterios, b3_template_id) =>
  api.post("/b3_consultas/generate_pdf_criterias", {
    b3_consulta_id,
    criterios,
    b3_template_id
  });

export const getTemplates = () => api.get("/b3_templates/query");

export const checkAcceptanceOfTermsAndPolicies = () =>
  api.get('/documentos_lgpd/check_acceptance_terms');

export const saveAcceptanceOfTermsAndPolicies = (termsOfUse, privacyPolicy) =>
  api.post(`/documentos_lgpd/save_acceptance_terms?termo_id=${termsOfUse}&politica_id=${privacyPolicy}`);

export const getDashboardData = (minDate = null, maxDate = null) =>
  api.get(`/b3_estatisticas/data?min_date=${minDate}&max_date=${maxDate}`);

export const hasAccessToDashboard = () =>
  api.get('/b3_estatisticas/has_access');

export const exportCoordinates = area_id =>
  api.get(`/b3_areas/export_coordinates/${area_id}`);

export const getCarLayers = resultId =>
  api.get(`/b3_areas/get_car_layers/${resultId}`);

export const linkCarToReceipt = data => {
  api.post("b3_consultas/vincula_car_proposta_sem_captcha", data);
};
